'use client';

import { useEffect } from 'react';
import dayjs from 'dayjs';
import { useForm, FieldValues, UseFormReturn } from 'react-hook-form';
import { SxProps } from '@mui/material';
import {
  SearchStationCalendarType,
  SearchStationCalendarResolver,
} from '@/schemas/SearchStationCalendarSchema';
import { service_RequestCalender } from '@/init/swagger';
import { CALENDAR_WEEKS, CALENDAR_ZONES } from '@/types/types';
import { RHFCheckbox } from '@/components/checkbox/RHFCheckbox';
import { SearchStationDetailType } from '@/schemas/SearchStationDetailSchema';
import { getSearchStationCalendarFilterConditionText } from '@/helpers/searchStationHelper';

type LogicProps = {
  formMethod: UseFormReturn<SearchStationDetailType>;
  close: () => void;
};

export const useLogic = ({ formMethod, close }: LogicProps) => {
  const { calendars, is_calendar_or: isCalendarOr } = formMethod.getValues();

  const { control, register, setValue, getValues, reset } =
    useForm<SearchStationCalendarType>({
      resolver: SearchStationCalendarResolver,
      defaultValues: {
        is_calendar_or_condition: 0,
        date_start_condition: dayjs().format('YYYY-MM-DD'),
        date_end_condition: dayjs().add(1, 'month').format('YYYY-MM-DD'),
        calendar_conditions: CALENDAR_ZONES.map((zone) => {
          return {
            is_sunday: false,
            is_monday: false,
            is_tuesday: false,
            is_wednesday: false,
            is_thursday: false,
            is_friday: false,
            is_saturday: false,
            zone_start: zone.start,
            zone_end: zone.end,
          };
        }) as Array<FieldValues>,
      },
    });

  useEffect(() => {
    if (!calendars?.length) {
      return;
    }

    setValue('is_calendar_or_condition', isCalendarOr);
    // date_start/end は配列内のどのcalendarでも同じなので、最初の値を使用
    setValue('date_start_condition', calendars[0].date_start);
    setValue('date_end_condition', calendars[0].date_end);
    setValue(
      'calendar_conditions',
      CALENDAR_ZONES.map((zone) => {
        const calendar = calendars.find((c) => {
          return zone.start === c.zone_start && zone.end === c.zone_end;
        });

        if (calendar && calendar?.weeks) {
          return {
            is_sunday: calendar.weeks.includes(CALENDAR_WEEKS.SUNDAY),
            is_monday: calendar.weeks.includes(CALENDAR_WEEKS.MONDAY),
            is_tuesday: calendar.weeks.includes(CALENDAR_WEEKS.TUESDAY),
            is_wednesday: calendar.weeks.includes(CALENDAR_WEEKS.WEDNESDAY),
            is_thursday: calendar.weeks.includes(CALENDAR_WEEKS.THURSDAY),
            is_friday: calendar.weeks.includes(CALENDAR_WEEKS.FRIDAY),
            is_saturday: calendar.weeks.includes(CALENDAR_WEEKS.SATURDAY),
            zone_start: zone.start,
            zone_end: zone.end,
          };
        } else {
          return {
            is_sunday: false,
            is_monday: false,
            is_tuesday: false,
            is_wednesday: false,
            is_thursday: false,
            is_friday: false,
            is_saturday: false,
            zone_start: zone.start,
            zone_end: zone.end,
          };
        }
      }) as Array<FieldValues>,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxToDate = dayjs().add(2, 'month').endOf('month');

  const HeadTableCellSX: SxProps = {
    textAlign: 'center',
    padding: '13px 0',
    '@media (max-width: 800px)': {
      padding: '23px 0',
    },
  };

  const BodyTableCellSX: SxProps = {
    textAlign: 'center',
    padding: '14.5px 0',
    '@media (max-width: 800px)': {
      padding: '16px 0',
    },
  };

  type ScheduleCheckboxProps = {
    name: string;
    control: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  };
  const ScheduleCheckbox: React.FC<ScheduleCheckboxProps> = ({
    name,
    control,
  }) => {
    return (
      <RHFCheckbox
        name={name}
        control={control}
        label={<></>}
        alignItems="center"
        formGroupSx={{
          backgroundColor: 'transparent',
          '@media (max-width: 800px)': {
            '.MuiFormControlLabel-root': {
              margin: 0,
            },
            '.MuiCheckbox-root': {
              padding: 0,
            },
          },
        }}
        sx={{
          '&.Mui-checked': {
            color: '#F3BBBB',
          },
        }}
      />
    );
  };

  const buildCalendars = (): service_RequestCalender[] => {
    const newCalendars: Array<service_RequestCalender> = [];

    const dateStart = dayjs(getValues('date_start_condition')).format(
      'YYYY-MM-DD',
    );
    const dateEnd = dayjs(getValues('date_end_condition')).format('YYYY-MM-DD');

    getValues('calendar_conditions')?.forEach((condition: FieldValues) => {
      const weeks: Array<number> = [];

      if (condition.is_sunday) {
        weeks.push(CALENDAR_WEEKS.SUNDAY);
      }
      if (condition.is_monday) {
        weeks.push(CALENDAR_WEEKS.MONDAY);
      }
      if (condition.is_tuesday) {
        weeks.push(CALENDAR_WEEKS.TUESDAY);
      }
      if (condition.is_wednesday) {
        weeks.push(CALENDAR_WEEKS.WEDNESDAY);
      }
      if (condition.is_thursday) {
        weeks.push(CALENDAR_WEEKS.THURSDAY);
      }
      if (condition.is_friday) {
        weeks.push(CALENDAR_WEEKS.FRIDAY);
      }
      if (condition.is_saturday) {
        weeks.push(CALENDAR_WEEKS.SATURDAY);
      }

      if (!weeks.length) {
        return;
      }

      newCalendars.push({
        date_start: dateStart,
        date_end: dateEnd,
        weeks: weeks,
        zone_start: condition.zone_start,
        zone_end: condition.zone_end,
      } as service_RequestCalender);
    });

    return newCalendars;
  };

  const handleFilter = () => {
    const newIsCalendarOr = getValues('is_calendar_or_condition');
    if (newIsCalendarOr === undefined) return;

    const newCalendars = buildCalendars();
    const newFilterConditionText = getSearchStationCalendarFilterConditionText({
      isCalendarOr: newIsCalendarOr,
      calendars: newCalendars,
    });

    formMethod.setValue('is_calendar_or', newIsCalendarOr);
    formMethod.setValue('calendars', newCalendars);
    formMethod.setValue('calendar_filter_condition', newFilterConditionText);

    close();
  };

  const handleClose = () => {
    close();
  };

  const handleReset = () => {
    formMethod.setValue('calendar_filter_condition', '');
    reset();
  };

  return {
    control,
    register,
    maxToDate,
    HeadTableCellSX,
    BodyTableCellSX,
    ScheduleCheckbox,
    handleFilter,
    handleClose,
    handleReset,
  };
};
